<template>
  <div class="zboard" :class="{ 'is-editing': editing }">
    <header class="zh">
      <div class="zh-text"><slot name="intro"></slot></div>
      <div class="zh-actions">
        <template v-if="!editing">
          <button type="button" class="zb" @click="$emit('refresh')" :disabled="refreshing" title="Refresh now">
            <i class="fas fa-sync-alt" :class="{ 'fa-spin': refreshing }"></i>
          </button>
          <button type="button" class="zb zb-primary" @click="startEditing"><i class="fas fa-sliders-h"></i> Customize</button>
        </template>
        <template v-else>
          <button type="button" class="zb" @click="drawer = true"><i class="fas fa-plus"></i> Cards <span class="zb-count" v-if="hiddenCount">{{ hiddenCount }}</span></button>
          <button type="button" class="zb" @click="resetLayout"><i class="fas fa-undo"></i> Reset</button>
          <button type="button" class="zb zb-primary" @click="stopEditing"><i class="fas fa-check"></i> Done</button>
        </template>
      </div>
    </header>

    <transition name="zfade">
      <div v-if="editing" class="zhint">
        <i class="fas fa-hand-pointer"></i>
        <span>Drag a card to move it, pull its bottom-right corner to resize it, <i class="fas fa-eye-slash"></i> to hide it. Everything is saved for you automatically.</span>
        <span class="zhint-state" :class="saveState">{{ saveLabel }}</span>
      </div>
    </transition>

    <div v-if="!layoutReady" class="zskeleton">
      <span v-for="n in 6" :key="n" :style="{ animationDelay: (n * 0.08) + 's' }"></span>
    </div>

    <grid-layout
      v-else-if="wide"
      :layout="visibleLayout"
      :col-num="COLUMNS"
      :row-height="ROW_HEIGHT"
      :margin="[18, 18]"
      :is-draggable="editing"
      :is-resizable="editing"
      :vertical-compact="true"
      :use-css-transforms="true"
      @layout-updated="onLayoutUpdated"
    >
      <grid-item
        v-for="(item, idx) in visibleLayout"
        :key="item.i"
        :i="item.i" :x="item.x" :y="item.y" :w="item.w" :h="item.h"
        :min-w="def(item.i).minW || 1" :min-h="def(item.i).minH || 1"
        drag-allow-from=".drag-handle"
        @resized="onResized"
      >
        <div class="zcell" :style="{ animationDelay: (Math.min(idx, 14) * 0.05) + 's' }">
          <stats-card :title="def(item.i).title" :icon="def(item.i).icon" :description="def(item.i).description" :editing="editing" :bare="isBare(item.i)" @hide="hide(item.i)">
            <component :is="def(item.i).component" :stats="stats" :icon="def(item.i).icon" v-bind="def(item.i).props || {}"/>
          </stats-card>
        </div>
      </grid-item>
    </grid-layout>

    <div v-else class="zstack">
      <div v-for="(item, idx) in stackOrder" :key="item.i" class="zcell" :class="{ half: item.w <= 3 }" :style="{ height: stackHeight(item) + 'px', animationDelay: (Math.min(idx, 14) * 0.05) + 's' }">
        <stats-card :title="def(item.i).title" :icon="def(item.i).icon" :description="def(item.i).description" :editing="editing" :bare="isBare(item.i)" @hide="hide(item.i)">
          <component :is="def(item.i).component" :stats="stats" :icon="def(item.i).icon" v-bind="def(item.i).props || {}"/>
        </stats-card>
      </div>
    </div>

    <!-- the catalog of cards -->
    <transition name="zdrawer">
      <div v-if="drawer" class="zd-backdrop" @click.self="drawer = false">
        <aside class="zd">
          <div class="zd-head">
            <div>
              <h2>Cards</h2>
              <p>Choose what you want on your page.</p>
            </div>
            <button type="button" class="zd-close" @click="drawer = false"><i class="fas fa-times"></i></button>
          </div>
          <div class="zd-body">
            <section v-for="cat in categories" :key="cat">
              <h3>{{ cat }}</h3>
              <label v-for="w in byCategory(cat)" :key="w.id" class="zd-item" :class="{ on: isVisible(w.id) }">
                <span class="zd-icon"><i :class="w.icon"></i></span>
                <span class="zd-text">
                  <span class="zd-title">{{ w.title }}</span>
                  <span class="zd-desc">{{ w.description }}</span>
                </span>
                <span class="zd-switch">
                  <input type="checkbox" :checked="isVisible(w.id)" @change="toggle(w.id, $event.target.checked)">
                  <span></span>
                </span>
              </label>
            </section>
          </div>
        </aside>
      </div>
    </transition>
  </div>
</template>

<script>
import { GridLayout, GridItem } from 'vue-grid-layout';
import Stats from '../../api/Stats';
import StatsCard from './StatsCard.vue';
import { CATEGORIES, COLUMNS, ROW_HEIGHT, byId, defaultLayout, mergeLayout, toSaved, widgetsFor } from './catalog';
import { watchTheme } from './theme';
import KpiTile from './widgets/KpiTile.vue';
import ImpactHero from './widgets/ImpactHero.vue';
import Co2Growth from './widgets/Co2Growth.vue';
import Forecast from './widgets/Forecast.vue';
import ForestGrowth from './widgets/ForestGrowth.vue';
import Species from './widgets/Species.vue';
import Countries from './widgets/Countries.vue';
import TopForests from './widgets/TopForests.vue';
import TokenFlow from './widgets/TokenFlow.vue';
import TreeStock from './widgets/TreeStock.vue';
import SplitsDelivered from './widgets/SplitsDelivered.vue';
import Retirements from './widgets/Retirements.vue';
import Customers from './widgets/Customers.vue';
import ScanCalendar from './widgets/ScanCalendar.vue';
import LiveFeed from './widgets/LiveFeed.vue';

// the map pulls in mapbox-gl: loaded only when the card is on the page
const ForestMap = () => import('./widgets/ForestMap.vue');

const WIDE_PX = 992;
const BARE = ['KpiTile', 'ImpactHero'];

/**
 * A board of statistics cards that each person arranges: drag, resize, hide, add from the catalog. The layout is saved
 * on the server per user and page (`page`: 'statistics' or 'home'), with a copy in this browser.
 *   board: which catalog and starting layout ('statistics', 'home-admin', 'home-user', see catalog.js)
 */
export default {
  name: 'StatsBoard',
  components: {
    GridLayout, GridItem, StatsCard,
    KpiTile, ImpactHero, Co2Growth, Forecast, ForestGrowth, Species, Countries, TopForests, TokenFlow, TreeStock,
    SplitsDelivered, Retirements, Customers, ScanCalendar, LiveFeed, ForestMap,
  },
  props: {
    stats: { type: Object, required: true },
    board: { type: String, default: 'statistics' },
    page: { type: String, default: 'statistics' },
    refreshing: { type: Boolean, default: false },
  },
  data() {
    return {
      COLUMNS, ROW_HEIGHT,
      layout: [],
      layoutReady: false,
      editing: false,
      drawer: false,
      saveState: 'idle',
      saveTimer: null,
      wide: window.innerWidth >= WIDE_PX,
      stopTheme: null,
    };
  },
  computed: {
    catalog() { return widgetsFor(this.board); },
    categories() { return CATEGORIES.filter((c) => this.catalog.some((w) => w.category === c)); },
    visibleLayout() { return this.layout.filter((l) => l.visible); },
    stackOrder() { return [...this.visibleLayout].sort((a, b) => a.y - b.y || a.x - b.x); },
    hiddenCount() { return this.layout.filter((l) => !l.visible).length; },
    localKey() { return `zc-board-${this.page}-${this.board}`; },
    saveLabel() {
      return { idle: '', saving: 'Saving…', saved: 'Saved', local: 'Saved on this device only' }[this.saveState] || '';
    }
  },
  created() {
    this.loadLayout();
  },
  mounted() {
    this.stopTheme = watchTheme();
    window.addEventListener('resize', this.onWindowResize);
  },
  beforeDestroy() {
    if (this.saveTimer) clearTimeout(this.saveTimer);
    if (this.stopTheme) this.stopTheme();
    window.removeEventListener('resize', this.onWindowResize);
  },
  methods: {
    def(id) { return byId(id) || { title: id, component: 'div' }; },
    isBare(id) { return BARE.includes(this.def(id).component); },
    byCategory(cat) { return this.catalog.filter((w) => w.category === cat); },
    isVisible(id) { const l = this.layout.find((x) => x.i === id); return !!(l && l.visible); },

    async loadLayout() {
      let saved = null;
      try {
        const { data } = await Stats.getLayout(this.page);
        saved = data;
      } catch (e) {
        try { saved = JSON.parse(localStorage.getItem(this.localKey) || 'null'); } catch (err) { saved = null; }
      }
      this.layout = mergeLayout(saved, this.board);
      this.layoutReady = true;
    },
    queueSave() {
      if (this.saveTimer) clearTimeout(this.saveTimer);
      this.saveState = 'saving';
      this.saveTimer = setTimeout(this.save, 800);
    },
    async save() {
      const body = toSaved(this.layout);
      try { localStorage.setItem(this.localKey, JSON.stringify(body)); } catch (e) { /* private mode */ }
      try {
        await Stats.saveLayout(body, this.page);
        this.saveState = 'saved';
      } catch (e) {
        this.saveState = 'local';
      }
    },
    onLayoutUpdated() {
      if (this.editing) this.queueSave();
    },
    onResized() {
      // charts follow their card; some libraries only listen to the window
      this.$nextTick(() => window.dispatchEvent(new Event('resize')));
    },
    bottom() { return this.visibleLayout.reduce((m, l) => Math.max(m, l.y + l.h), 0); },
    hide(id) {
      const l = this.layout.find((x) => x.i === id);
      if (l) { l.visible = false; this.queueSave(); }
    },
    toggle(id, on) {
      const l = this.layout.find((x) => x.i === id);
      if (!l) return;
      if (on && !l.visible) { l.y = this.bottom(); l.x = 0; }
      l.visible = on;
      this.queueSave();
    },
    resetLayout() {
      this.layout = defaultLayout(this.board);
      this.queueSave();
    },
    startEditing() { this.editing = true; this.saveState = 'idle'; },
    stopEditing() { this.editing = false; this.drawer = false; },
    onWindowResize() { this.wide = window.innerWidth >= WIDE_PX; },
    stackHeight(item) { return Math.max(150, item.h * ROW_HEIGHT + (item.h - 1) * 18); },
  }
};
</script>

<style scoped lang="scss">
.zboard {
  position: relative;
  padding-bottom: 24px;

  /* dragging cards must not select the text of the page */
  &.is-editing { user-select: none; }
}

/* ---------------------------------------------------------------- header */
.zh {
  display: flex;
  flex-wrap: wrap;
  align-items: flex-end;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 14px;
  animation: zup 0.6s cubic-bezier(.2, .8, .2, 1) both;
}

.zh-text { min-width: 0; }
.zh-actions { display: flex; flex-wrap: wrap; gap: 8px; }

.zb {
  display: inline-flex;
  align-items: center;
  gap: 7px;
  border: 1px solid var(--border-color);
  background: var(--card-bg);
  color: var(--text-main);
  font-weight: 700;
  font-size: 0.85rem;
  padding: 9px 16px;
  border-radius: 12px;
  cursor: pointer;
  box-shadow: var(--shadow-sm);
  transition: transform 0.2s, box-shadow 0.2s, background 0.2s, color 0.2s;

  &:hover:not(:disabled) { transform: translateY(-1px); box-shadow: var(--shadow-md); }
  &:disabled { opacity: 0.6; cursor: default; }
}

.zb-primary {
  border-color: transparent;
  color: #fff;
  background: var(--accent-gradient);
  box-shadow: var(--shadow-glow);
}

.zb-count {
  min-width: 20px;
  height: 20px;
  padding: 0 6px;
  border-radius: 999px;
  font-size: 0.7rem;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: #fff;
  background: var(--co2-accent);
}

.zhint {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 10px;
  margin: 0 0 6px;
  padding: 10px 14px;
  border-radius: 14px;
  font-size: 0.84rem;
  color: var(--text-main);
  background: rgba(var(--accent-green-rgb), 0.1);
  border: 1px dashed rgba(var(--accent-green-rgb), 0.45);

  > i { color: var(--accent-green); }
}

.zhint-state {
  margin-left: auto;
  font-weight: 700;
  font-size: 0.78rem;
  color: var(--text-muted);
  &.saved { color: var(--accent-green); }
  &.local { color: #d08a1e; }
}

/* ---------------------------------------------------------------- grid */
.zcell {
  height: 100%;
  animation: zup 0.7s cubic-bezier(.2, .8, .2, 1) both;
}

@keyframes zup {
  from { opacity: 0; transform: translateY(16px) scale(0.985); }
  to { opacity: 1; transform: translateY(0) scale(1); }
}

::v-deep .vue-grid-layout { margin: 0 -18px; transition: height 0.25s ease; }
::v-deep .vue-grid-item { transition: transform 0.25s ease, width 0.25s ease, height 0.25s ease; }
::v-deep .vue-grid-item.vue-draggable-dragging { transition: none; z-index: 20; }
::v-deep .vue-grid-item.vue-draggable-dragging .zcell { transform: rotate(0.6deg) scale(1.01); filter: drop-shadow(0 24px 40px rgba(0, 0, 0, 0.25)); }
::v-deep .vue-grid-item.vue-grid-placeholder {
  background: rgba(var(--accent-green-rgb), 0.18);
  border: 2px dashed rgba(var(--accent-green-rgb), 0.7);
  border-radius: 20px;
  opacity: 1;
  z-index: 2;
}
::v-deep .vue-grid-item > .vue-resizable-handle {
  z-index: 30;
  width: 22px;
  height: 22px;
  right: 6px;
  bottom: 6px;
  background: none;
  border-right: 3px solid var(--accent-green);
  border-bottom: 3px solid var(--accent-green);
  border-radius: 0 0 8px 0;
  cursor: se-resize;
  opacity: 0.9;
}

.zstack {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 14px;

  .zcell { grid-column: span 2; }
  .zcell.half { grid-column: span 1; }
}

@media (max-width: 560px) {
  .zstack .zcell.half { grid-column: span 2; }
}

.zskeleton {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 18px;

  span {
    height: 220px;
    border-radius: 20px;
    background: linear-gradient(90deg, var(--card-bg) 0%, var(--bg-secondary) 50%, var(--card-bg) 100%);
    background-size: 200% 100%;
    animation: zshimmer 1.4s infinite, zup 0.6s both;
  }
}

@keyframes zshimmer { from { background-position: 200% 0; } to { background-position: -200% 0; } }

/* ---------------------------------------------------------------- catalog drawer */
.zd-backdrop {
  position: fixed;
  inset: 0;
  z-index: 1100;
  background: rgba(0, 0, 0, 0.35);
  backdrop-filter: blur(3px);
  display: flex;
  justify-content: flex-end;
}

.zd {
  width: min(420px, 100%);
  height: 100%;
  background: var(--bg-primary);
  box-shadow: var(--shadow-lg);
  display: flex;
  flex-direction: column;
}

.zd-head {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  padding: 22px 22px 10px;

  h2 { margin: 0; font-size: 1.3rem; font-weight: 800; color: var(--text-heading); }
  p { margin: 2px 0 0; color: var(--text-muted); font-size: 0.85rem; }
}

.zd-close {
  border: none;
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background: var(--bg-secondary);
  color: var(--text-main);
  cursor: pointer;
}

.zd-body {
  flex: 1 1 auto;
  overflow: auto;
  padding: 4px 18px 24px;

  h3 {
    margin: 16px 4px 8px;
    font-size: 0.72rem;
    font-weight: 800;
    letter-spacing: 0.1em;
    text-transform: uppercase;
    color: var(--text-muted);
  }
}

.zd-item {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 10px 12px;
  margin-bottom: 6px;
  border-radius: 14px;
  background: var(--card-bg);
  border: 1px solid var(--border-color);
  cursor: pointer;
  transition: border-color 0.2s, transform 0.2s;

  &:hover { transform: translateX(-2px); }
  &.on { border-color: rgba(var(--accent-green-rgb), 0.55); }
}

.zd-icon {
  width: 36px;
  height: 36px;
  flex: 0 0 36px;
  border-radius: 11px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: var(--accent-green);
  background: rgba(var(--accent-green-rgb), 0.12);
}

.zd-text { flex: 1 1 auto; min-width: 0; display: flex; flex-direction: column; }
.zd-title { font-weight: 700; color: var(--text-heading); font-size: 0.88rem; }
.zd-desc { font-size: 0.74rem; color: var(--text-muted); line-height: 1.3; }

.zd-switch {
  position: relative;
  flex: 0 0 40px;
  height: 22px;

  input { position: absolute; opacity: 0; inset: 0; margin: 0; cursor: pointer; z-index: 1; }

  span {
    position: absolute;
    inset: 0;
    border-radius: 999px;
    background: var(--border-color);
    transition: background 0.25s;

    &::after {
      content: '';
      position: absolute;
      top: 3px;
      left: 3px;
      width: 16px;
      height: 16px;
      border-radius: 50%;
      background: #fff;
      box-shadow: 0 1px 3px rgba(0, 0, 0, 0.25);
      transition: transform 0.25s cubic-bezier(.2, .8, .2, 1);
    }
  }

  input:checked + span { background: var(--accent-green); }
  input:checked + span::after { transform: translateX(18px); }
}

.zdrawer-enter-active, .zdrawer-leave-active { transition: opacity 0.25s; .zd { transition: transform 0.35s cubic-bezier(.2, .8, .2, 1); } }
.zdrawer-enter, .zdrawer-leave-to { opacity: 0; .zd { transform: translateX(40px); } }
.zfade-enter-active, .zfade-leave-active { transition: opacity 0.25s, transform 0.25s; }
.zfade-enter, .zfade-leave-to { opacity: 0; transform: translateY(-6px); }
</style>
